import { useEffect } from 'react';
import { usePaddleConnect } from '@modules/paddle-connect/hooks/usePaddleConnect';

/** Lifted once per catalog section; never fetch connection status per plan row. */
export interface PaddlePickerConnectionProps {
  paddleSandboxConnected?: boolean;
  paddleProductionConnected?: boolean;
  paddleSandboxNotConnectedReason?: string | null;
  paddleProductionNotConnectedReason?: string | null;
}

export function usePaddlePickerConnection(projectId: string): PaddlePickerConnectionProps {
  const { status, loading, error, refreshStatus } = usePaddleConnect(projectId);
  useEffect(() => {
    const refresh = () => {
      if (projectId && document.visibilityState === 'visible') void refreshStatus().catch(() => {});
    };
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [projectId, refreshStatus]);
  const checking = loading || (!status && !error);
  const reason = (environment: 'sandbox' | 'production') => {
    if (checking) return 'Checking Paddle connection…';
    if (error) return `Cannot verify Paddle ${environment} connection — check Settings → Integrations`;
    if (!status?.[environment]?.connected) return `Connect Paddle ${environment === 'sandbox' ? 'Sandbox' : 'Production'} in Settings → Integrations`;
    return null;
  };
  return {
    paddleSandboxConnected: !checking && !error && status?.sandbox?.connected === true,
    paddleProductionConnected: !checking && !error && status?.production?.connected === true,
    paddleSandboxNotConnectedReason: reason('sandbox'),
    paddleProductionNotConnectedReason: reason('production'),
  };
}
